import { computed, watch, type Ref } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router';
import { isEqual } from '~/utils/helpers';

const stripIgnored = (obj: object): LocationQueryRaw =>
    Object.fromEntries(
        Object.entries(obj).filter(([key, value]) => {
            if (key === 'cursor') {
                return false;
            }

            if (value === '' || value === undefined || value === null) {
                return false;
            }

            if (Array.isArray(value) && value.length === 0) {
                return false;
            }

            return true;
        })
    ) as LocationQueryRaw;

const stringifyValues = (obj: object): Record<string, string> =>
    Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, String(value)]));

export const useFilterPersistence = (storageKey: string, fetchParams: Ref<object>, defaultFilters: () => object) => {
    const router = useRouter();
    const route = useRoute();

    const initializeFilters = () => {
        let filters;

        // gives preference to query, in case the user access the page from a direct link
        if (Object.keys(route.query).length > 0) {
            filters = route.query;
        } else {
            const stored = sessionStorage.getItem(storageKey);

            try {
                filters = stored ? JSON.parse(stored) : null;
            } catch (e) {
                console.error(`Failed to parse stored filters from ${storageKey}:`, e);
                filters = null;
            }
        }

        fetchParams.value = filters && Object.keys(filters).length > 0 ? filters : defaultFilters();
    };

    let lastSerialized = '';

    const syncFiltersToStorage = (newParams: object) => {
        const stripped = stripIgnored(newParams);
        const serialized = JSON.stringify(stripped);
        if (serialized === lastSerialized) {
            return;
        }
        lastSerialized = serialized;

        try {
            sessionStorage.setItem(storageKey, serialized);
        } catch (e) {
            console.error(`Failed to save filters to ${storageKey}:`, e);
        }

        router.replace({
            query: stripped,
        });
    };

    initializeFilters();
    lastSerialized = JSON.stringify(stripIgnored(fetchParams.value));

    watch(fetchParams, syncFiltersToStorage, { deep: true });

    const isDefault = computed(() =>
        isEqual(stringifyValues(stripIgnored(defaultFilters())), stringifyValues(stripIgnored(fetchParams.value)))
    );

    return { isDefault };
};
